import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { colors } from '../../../../../theme/colors';
import { A4_ASPECT_RATIO } from '../constants';
import { DropTarget, TearsheetV2Layout } from '../types';
import { CellRenderer } from './CellRenderer';

const canvasWrapperStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 1.5rem;
  background: ${colors.neutral[10]};
  overflow: auto;
`;

const a4ContainerStyle = css`
  /* Use explicit dimensions instead of aspect-ratio for react-resizable-panels compatibility */
  width: min(600px, 100%);
  height: min(calc(600px * ${A4_ASPECT_RATIO}), calc(100vh - 250px));
  background: white;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid ${colors.neutral[20]};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const emptyCanvasStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 2px dashed ${colors.neutral[30]};
  border-radius: 4px;
  background: ${colors.neutral[5]};
  transition: all 0.2s ease;
`;

const emptyCanvasActiveStyle = css`
  border-color: ${colors.primary[40]};
  background: ${colors.primary[5]};
`;

interface LayoutCanvasProps {
  layout: TearsheetV2Layout;
  activeDropTarget?: DropTarget | null;
  onRemovePanel?: (cellId: string) => void;
  onSizesChange?: (boxCellId: string, sizes: number[]) => void;
}

export function LayoutCanvas({ layout, activeDropTarget, onRemovePanel, onSizesChange }: LayoutCanvasProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-root',
    data: { type: 'canvas-root' },
  });

  const hasContent = layout.root !== null;

  return (
    <div css={canvasWrapperStyle}>
      <div css={a4ContainerStyle} ref={setNodeRef} data-droppable-id='canvas-root'>
        {hasContent ? (
          <CellRenderer
            cell={layout.root!}
            activeDropTarget={activeDropTarget}
            onRemovePanel={onRemovePanel}
            onSizesChange={onSizesChange}
          />
        ) : (
          <div css={[emptyCanvasStyle, isOver && emptyCanvasActiveStyle]}>
            <Typography variant='body1' color='text.secondary'>
              Drag a panel here to start
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              Build your tearsheet layout
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}
